// App.js
import React, { useState, useEffect } from "react";
import { db } from "./firebase";
import {
  collection,
  addDoc,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  query,
  where,
} from "firebase/firestore";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import logo from "./logo.jpg";
import './index.css';

// Специалности
const specialties = {
  drums: "Барабани",
  guitar: "Китара",
  bass: "Бас китара",
  singing: "Пеене",
  piano: "Пиано",
  harmony: "Хармония",
};

// Дни
const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

// Генериране на часове 08:00–20:00 (45 мин)
const generateHours = () => {
  const hours = [];
  for (let h = 8; h <= 20; h++) {
    const start = `${h.toString().padStart(2, "0")}:00`;
    const end = `${h.toString().padStart(2, "0")}:45`;
    hours.push(`${start} - ${end}`);
  }
  return hours;
};
const hours = generateHours();

function App() {
  const [slots, setSlots] = useState([]);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [childName, setChildName] = useState("");
  const [phone, setPhone] = useState("");
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [editMode, setEditMode] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [editDay, setEditDay] = useState(null);
  const [editTime, setEditTime] = useState(null);

  const [expandedDay, setExpandedDay] = useState(null);

  const queryParams = new URLSearchParams(window.location.search);
  const adminId = queryParams.get("admin");
  const isAdmin = !!adminId && specialties[adminId];

  // LOAD SLOTS
  useEffect(() => {
    const unsub = onSnapshot(collection(db, "slots"), snapshot => {
      const data = [];
      snapshot.forEach(doc => data.push({ id: doc.id, ...doc.data() }));
      data.sort((a, b) => {
        const dayDiff = days.indexOf(a.day) - days.indexOf(b.day);
        if (dayDiff !== 0) return dayDiff;
        const aHour = parseInt(a.time.split(":")[0]);
        const bHour = parseInt(b.time.split(":")[0]);
        return aHour - bHour;
      });
      setSlots(data);
    });
    return () => unsub();
  }, []);

  // SLOTS BY DAY
  const slotsByDay = {};
  for (let day of days) {
    slotsByDay[day] = slots.filter(slot => slot.day === day);
  }

  // POPULATE SLOTS
  const populate = async () => {
    if (!isAdmin) return;
    const slotsSnapshot = await getDocs(
      query(collection(db, "slots"), where("specialtyId", "==", adminId))
    );
    if (!slotsSnapshot.empty) {
      toast.info("ℹ️ Графикът вече е създаден!");
      return;
    }
    for (let day of days) {
      for (let time of hours) {
        await addDoc(collection(db, "slots"), {
          day,
          time,
          specialty: specialties[adminId],
          specialtyId: adminId,
          reserved: false,
          blocked: false,
          childName: null,
          phone: null,
        });
      }
    }
    toast.success("✅ Графикът е създаден!");
  };

  // ADMIN FUNCTIONS
  const toggleBlockDay = async (day) => {
    const daySlots = slotsByDay[day]?.filter(s => s.specialtyId === adminId);
    if (!daySlots || daySlots.length === 0) return;
    const allBlocked = daySlots.every(s => s.blocked);
    for (let slot of daySlots) {
      await updateDoc(doc(db, "slots", slot.id), { blocked: !allBlocked });
    }
  };

  const toggleBlockSlot = async (slot) => {
    await updateDoc(doc(db, "slots", slot.id), { blocked: !slot.blocked });
  };

  const startEdit = (slot) => {
    setEditMode(true);
    setEditSlot(slot);
    setEditDay(slot.day);
    setEditTime(slot.time);
  };

  // BLOCK OTHER SPECIALTIES
  const blockOtherSpecialties = async (day, time, exceptId) => {
    const otherSlots = slots.filter(s => s.day === day && s.time === time && s.id !== exceptId);
    for (let s of otherSlots) {
      await updateDoc(doc(db, "slots", s.id), { blocked: true });
    }
  };

  const getFreeSlots = (specialty, day) => {
    return slotsByDay[day]?.filter(s => s.specialty === specialty && !s.reserved && !s.blocked);
  };

  // EDIT HELPERS
  const getFreeDaysForEdit = (slot) => {
    return days.filter(d => {
      const freeSlots = getFreeSlots(slot.specialty, d);
      return freeSlots.length > 0 || slot.day === d; // включваме текущия ден
    });
  };

  const getFreeTimesForEdit = (slot, day) => {
    const freeTimes = getFreeSlots(slot.specialty, day)?.map(s => s.time) || [];
    if (slot.day === day && !freeTimes.includes(slot.time)) freeTimes.push(slot.time); // включваме текущия час
    return freeTimes;
  };

  // Групиране по време за родител
  const slotsByTime = (day) => {
    const slotsOfDay = slotsByDay[day] || [];
    const times = [...new Set(slotsOfDay.map(s => s.time))];
    return times.map(time => {
      const slotsAtTime = slotsOfDay.filter(s => s.time === time);
      const availableSlot = slotsAtTime.find(s => s.specialty === selectedInstrument && !s.reserved && !s.blocked);
      const otherReservedSlot = slotsAtTime.find(s => s.specialty !== selectedInstrument && s.reserved);
      return { time, availableSlot, otherReservedSlot };
    });
  };

  const getSlotsByHour = (day) => {
    const daySlots = slotsByDay[day] || [];
    const uniqueHours = [...new Set(daySlots.map(s => s.time))];
    return uniqueHours.map(hour => {
      const slotsAtHour = daySlots.filter(s => s.time === hour);
      return { hour, slotsAtHour };
    });
  };

  return (
    <div style={{
      padding: 20,
      minHeight: "100vh",
      overflowX: "auto",
      filter: "brightness(90%)",
    }}>
      <h1 style={{ textAlign: "center", marginBottom: 30 }}>Studio Central – график за уроци 🎵</h1>

      {/* ADMIN VIEW */}
      {isAdmin && (
        <div
          style={{
            backgroundImage: `url(${logo})`,
            backgroundSize: "cover",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            padding: 20,
            minHeight: "100vh",
          }}
        >
          <h2>Админ: {specialties[adminId]}</h2>
          <div
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              width: "100%",
            }}
          >
            <button
              onClick={populate}
              style={{
                padding: "8px 16px",
                borderRadius: 6,
                border: "none",
                backgroundColor: "#007bff",
                color: "white",
                cursor: "pointer",
              }}
            >
              Създай график (само първия път)
            </button>
          </div>

          <div
            style={{
              display: "flex",
              gap: 12,
              overflowX: "auto",
              width: "100%",
            }}
          >
            {days.map((day) => (
              <div
                key={day}
                style={{
                  flex: "0 0 280px",
                  background: "rgba(255,255,255,0.95)",
                  borderRadius: 10,
                  padding: 10,
                }}
              >
                <h3>{day}</h3>
                <button
                  onClick={() => toggleBlockDay(day)}
                  style={{
                    marginBottom: 6,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "none",
                    cursor: "pointer",
                    backgroundColor: "#007bff",
                    color: "white"
                  }}
                >
                  {slotsByDay[day]?.filter(s => s.specialtyId === adminId).every(s => s.blocked)
                    ? "Отблокирай целия ден"
                    : "Блокирай целия ден"}
                </button>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {getSlotsByHour(day).map(({ hour, slotsAtHour }) => {
                    const mySlot = slotsAtHour.find(s => s.specialtyId === adminId);
                    const otherSlot = slotsAtHour.find(s => s.specialtyId !== adminId && s.reserved);

                    let bgColor = "#d1e7dd"; // свободен
                    if (mySlot?.blocked) bgColor = "#f8d7da";
                    if (otherSlot) bgColor = "#e0e0e0"; // зает от друг инструмент
                    if (mySlot?.reserved) bgColor = "#fff3cd";

                    return (
                      <li key={hour} style={{ marginBottom: 4, backgroundColor: bgColor, padding: 6, borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>
                          {hour}{" "}
                          {mySlot?.reserved
                            ? `– ${mySlot.childName}, ${mySlot.phone}`
                            : otherSlot
                              ? `– Урок по ${otherSlot.specialty}`
                              : mySlot?.blocked
                                ? "– Блокиран"
                                : ""}
                        </span>
                        <div style={{ display: "flex", gap: 4 }}>
                          {mySlot && (
                            <button onClick={() => toggleBlockSlot(mySlot)} style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "none", backgroundColor: mySlot.blocked ? "#ffc107" : "#dc3545", color: "white", cursor: "pointer" }}>
                              {mySlot.blocked ? "Отблокирай" : "Блокирай"}
                            </button>
                          )}
                          {mySlot?.reserved && (
                            <>
                              <button onClick={() => startEdit(mySlot)} style={{ fontSize: 12, padding: "4px 6px", borderRadius: 4, border: "none", backgroundColor: "#007bff", color: "white", cursor: "pointer" }}>Редактирай</button>
                              <button onClick={async () => {
                                await updateDoc(doc(db, "slots", mySlot.id), { reserved: false, childName: null, phone: null });
                                toast.info(`❌ Часът на ${mySlot.childName} е отменен`);
                              }} style={{ padding: "4px 8px", borderRadius: 6, border: "none", cursor: "pointer", backgroundColor: "#dc3545", color: "white", fontSize: 12 }}>Откажи</button>
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* PARENT VIEW */}
      {!isAdmin && (
        <>
          <h2 style={{ textAlign: "center" }}>Избери инструмент:</h2>
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginBottom: 20, flexWrap: "wrap" }}>
            {Object.entries(specialties).map(([id, name]) => (
              <button
                key={id}
                onClick={() => setSelectedInstrument(name)}
                style={{
                  padding: "8px 12px",
                  borderRadius: 8,
                  border: selectedInstrument === name ? "2px solid blue" : "1px solid gray",
                  background: selectedInstrument === name ? "#e6f0ff" : "white",
                  cursor: "pointer",
                }}
              >
                {name}
              </button>
            ))}
          </div>

          {selectedInstrument && days.map(day => (
            <div key={day} style={{ marginBottom: 15 }}>
              <h3
                onClick={() => setExpandedDay(expandedDay === day ? null : day)}
                style={{ cursor: "pointer", background: "#f0f0f0", padding: 6, borderRadius: 6 }}
              >
                {day} {expandedDay === day ? "▲" : "▼"}
              </h3>
              {expandedDay === day && (
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {slotsByTime(day).map(({ time, availableSlot, otherReservedSlot }) => {
                    // Ако слотът е зает от друг инструмент, родителят го вижда сиво и без текст
                    const isDisabled = !!otherReservedSlot;

                    return (
                      <li
                        key={time}
                        onClick={() => { if (availableSlot && !isDisabled) { setSelectedSlot(availableSlot); setShowModal(true); } }}
                        style={{
                          backgroundColor: availableSlot && !isDisabled ? "#c8f7c5" : "#e0e0e0",
                          margin: 4,
                          padding: 6,
                          borderRadius: 6,
                          cursor: availableSlot && !isDisabled ? "pointer" : "not-allowed",
                          textAlign: "center",
                          color: "#000",
                        }}
                      >
                        {time}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          ))}
        </>
      )}


      {/* MODAL */}
      {(showModal || editMode) && (
        <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ background: "white", padding: 20, borderRadius: 12, width: "90%", maxWidth: 400, boxSizing: "border-box" }}>
            {editMode && editSlot && (
              <>
                <h3 style={{ marginBottom: 10 }}>Редакция: {editSlot.time} ({editSlot.day})</h3>
                <div style={{ marginBottom: 10 }}>
                  <label>Ден:</label>
                  <select value={editDay} onChange={e => setEditDay(e.target.value)} style={{ marginLeft: 6, padding: 4, borderRadius: 4 }}>
                    {getFreeDaysForEdit(editSlot).map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 15 }}>
                  <label>Час:</label>
                  <select value={editTime} onChange={e => setEditTime(e.target.value)} style={{ marginLeft: 6, padding: 4, borderRadius: 4 }}>
                    {getFreeTimesForEdit(editSlot, editDay).map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button onClick={() => { setEditMode(false); setEditSlot(null); }} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid gray" }}>Откажи</button>
                  <button onClick={async () => {
                    if (!editSlot || !editDay || !editTime) return;

                    // Премахваме стария слот
                    await updateDoc(doc(db, "slots", editSlot.id), { reserved: false, childName: null, phone: null });

                    // Взимаме новия слот
                    const newSlot = slotsByDay[editDay].find(s => s.time === editTime && s.specialty === editSlot.specialty);

                    if (newSlot) {
                      await updateDoc(doc(db, "slots", newSlot.id), { reserved: true, childName: editSlot.childName, phone: editSlot.phone });
                      await blockOtherSpecialties(editDay, editTime, newSlot.id);
                      toast.success("✅ Часът е преместен успешно!");
                    }
                    setEditMode(false); setEditSlot(null);
                  }} style={{ padding: "6px 12px", borderRadius: 6, border: "none", backgroundColor: "blue", color: "white", cursor: "pointer" }}>Запази</button>
                </div>
              </>
            )}

            {!editMode && selectedSlot && (
              <>
                <h2 style={{ marginBottom: 8, textAlign: "center" }}>{selectedInstrument} – {selectedSlot.time}</h2>
                <div style={{ textAlign: "center", color: "#444", marginBottom: 12 }}>
                  <p style={{ margin: 4, fontWeight: "bold" }}>Първи урок – безплатен</p>
                  <p style={{ margin: 4 }}>1 урок – 40лв(€20,45)</p>
                  <p style={{ margin: 4 }}>4 урока – 140лв(€71,58)</p>
                </div>
                <input type="text" placeholder="Две имена на ученика" value={childName} onChange={e => setChildName(e.target.value)} style={{ width: "100%", padding: 8, marginBottom: 8, borderRadius: 6, border: "1px solid #ccc" }} />
                <input type="text" placeholder="Телефонен номер" value={phone} onChange={e => setPhone(e.target.value)} style={{ width: "100%", padding: 8, marginBottom: 12, borderRadius: 6, border: "1px solid #ccc" }} />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
                  <button onClick={() => setShowModal(false)} style={{ padding: "6px 12px", borderRadius: 6, border: "1px solid gray" }}>Откажи</button>
                  <button onClick={async () => {
                    if (!childName || !phone) return;
                    await updateDoc(doc(db, "slots", selectedSlot.id), { reserved: true, childName, phone });
                    await blockOtherSpecialties(selectedSlot.day, selectedSlot.time, selectedSlot.id);
                    setShowModal(false);
                    setChildName(""); setPhone(""); setSelectedSlot(null);
                    toast.success(`✅ Урокът по ${selectedInstrument} в ${selectedSlot.time} е запазен!`);
                  }} style={{ padding: "6px 12px", borderRadius: 6, border: "none", backgroundColor: "blue", color: "white", cursor: "pointer" }}>Запази</button>
                </div>
                <ToastContainer position="top-center" autoClose={3000} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
